import { convertMoneyToNumber } from './currency';
import { createSlug } from './urlHelpers';
import { pathByRouteName } from './routes';
import { categoryNameForSEO, categoryOptionsFromConfig } from './categorySEO';

const collapseWhitespace = value => (value || '').replace(/\s+/g, ' ').trim();

export const truncateSEOText = (value, maxLength) => {
  const text = collapseWhitespace(value);
  if (text.length <= maxLength) {
    return text;
  }

  const shortened = text.slice(0, maxLength - 1);
  const lastSpace = shortened.lastIndexOf(' ');
  return `${shortened.slice(0, lastSpace > maxLength * 0.65 ? lastSpace : maxLength - 1)}…`;
};

export const listingLocationName = publicData => {
  const address = collapseWhitespace(publicData?.location?.address);
  if (!address) {
    return '';
  }

  const parts = address.split(',').map(part => part.trim()).filter(Boolean);
  const partsWithoutStreet =
    parts.length > 2 && /\d/.test(parts[0]) ? parts.slice(1) : parts;
  const localityPartsWithCountry =
    partsWithoutStreet.length > 3 ? partsWithoutStreet.slice(-3) : partsWithoutStreet;
  const lastPart = localityPartsWithCountry[localityPartsWithCountry.length - 1]?.toLowerCase();
  const localityParts = ['united states', 'united states of america', 'usa', 'us'].includes(lastPart)
    ? localityPartsWithCountry.slice(0, -1)
    : localityPartsWithCountry;
  return localityParts
    .map(part => collapseWhitespace(part.replace(/\b\d{4,}\b/g, '')))
    .filter(Boolean)
    .join(', ');
};

export const listingCategoryLabel = (publicData, config) => {
  const category = publicData?.category;
  const label = categoryOptionsFromConfig(config).find(option => option.option === category)?.label;
  return categoryNameForSEO(category, label);
};

export const createListingCanonicalPath = (listing, routeConfiguration) =>
  pathByRouteName('ListingPage', routeConfiguration, {
    id: listing.id.uuid,
    slug: createSlug(listing.attributes.title || ''),
  });

const supportedAmenityNames = {
  ac: 'ac',
  bbqGrill: 'outdoorGrill',
  centralHeating: 'heating',
  freeWifi: 'wifi',
  gym: 'gymFitnessEquipment',
  hotTub: 'hotTub',
  ironingBoard: 'ironingBoard',
  kitchen: 'kitchen',
  patio: 'patio',
  pool: 'pool',
  tv: 'tv',
  washingMachine: 'washerDryer',
  wifi: 'wifi',
};

const accommodationDetails = publicData => {
  const maxPeople = Number.parseInt(publicData?.maxPeople, 10);
  const bedrooms = Number.parseInt(publicData?.bedrooms || publicData?.beds, 10);
  const bathrooms = Number.parseFloat(publicData?.bathrooms);
  const amenities = Array.isArray(publicData?.amenities) ? publicData.amenities : [];
  const amenityFeature = amenities
    .map(value => supportedAmenityNames[value])
    .filter(Boolean)
    .map(name => ({ '@type': 'LocationFeatureSpecification', name, value: true }));

  return {
    ...(Number.isInteger(maxPeople) && maxPeople > 0
      ? { occupancy: { '@type': 'QuantitativeValue', value: maxPeople } }
      : {}),
    ...(Number.isInteger(bedrooms) && bedrooms > 0 ? { numberOfBedrooms: bedrooms } : {}),
    ...(Number.isFinite(bathrooms) && bathrooms > 0
      ? { numberOfBathroomsTotal: bathrooms }
      : {}),
    ...(amenityFeature.length > 0 ? { amenityFeature } : {}),
    additionalType: publicData?.type === 'room' ? 'PrivateRoom' : 'EntirePlace',
  };
};

export const createListingSEO = ({
  listing,
  config,
  routeConfiguration,
  images,
  currentStock,
}) => {
  const { title = '', description = '', publicData = {}, price, geolocation } = listing.attributes;
  const listingDescription = collapseWhitespace(description);
  const rootURL = config.marketplaceRootURL.replace(/\/+$/, '');
  const canonicalPath = createListingCanonicalPath(listing, routeConfiguration);
  const canonicalURL = `${rootURL}${canonicalPath}`;
  const category = listingCategoryLabel(publicData, config);
  const categorySlug = publicData.category;
  const location = listingLocationName(publicData);
  const titleSuffix = ` | ${config.marketplaceName}`;
  const titleLocation = location ? ` in ${location}` : '';
  const seoTitle = `${truncateSEOText(
    `${title}${titleLocation}`,
    Math.max(30, 60 - titleSuffix.length)
  )}${titleSuffix}`;
  const listingType = category ? `${category.toLowerCase()} vacation rental` : 'vacation rental';
  const locationPhrase = location ? ` in ${location}` : '';
  const descriptionLead = `Book ${title}, a ${listingType}${locationPhrase}, on ${config.marketplaceName}.`;
  const seoDescription = truncateSEOText(`${descriptionLead} ${listingDescription}`, 158);
  const imageURLs = (images || []).map(image => image.url).filter(Boolean);
  const latitude = geolocation?.lat;
  const longitude = geolocation?.lng;
  const details = accommodationDetails(publicData);
  const hasOccupancy = !!details.occupancy;
  const qualifiesForVacationRentalSchema =
    imageURLs.length >= 8 &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    hasOccupancy;
  const address = collapseWhitespace(publicData?.location?.address);
  const addressMaybe = address
    ? { address: { '@type': 'PostalAddress', streetAddress: address } }
    : {};
  const geoMaybe =
    Number.isFinite(latitude) && Number.isFinite(longitude)
      ? { geo: { '@type': 'GeoCoordinates', latitude, longitude } }
      : {};

  const accommodationSchema = qualifiesForVacationRentalSchema
    ? {
        '@context': 'https://schema.org',
        '@type': 'VacationRental',
        '@id': `${canonicalURL}#vacation-rental`,
        identifier: listing.id.uuid,
        name: title,
        description: listingDescription || seoDescription,
        url: canonicalURL,
        image: imageURLs,
        ...addressMaybe,
        ...geoMaybe,
        containsPlace: {
          '@type': 'Accommodation',
          ...details,
        },
      }
    : {
        '@context': 'https://schema.org',
        '@type': 'Accommodation',
        '@id': `${canonicalURL}#accommodation`,
        identifier: listing.id.uuid,
        name: title,
        description: listingDescription || seoDescription,
        url: canonicalURL,
        ...(imageURLs.length > 0 ? { image: imageURLs } : {}),
        ...addressMaybe,
        ...geoMaybe,
        ...details,
      };

  const hasPrice = price && Number.isFinite(convertMoneyToNumber(price));
  const offerSchema = hasPrice
    ? {
        '@context': 'https://schema.org',
        '@type': 'Offer',
        '@id': `${canonicalURL}#offer`,
        url: canonicalURL,
        price: convertMoneyToNumber(price).toFixed(2),
        priceCurrency: price.currency,
        ...(typeof currentStock === 'number'
          ? {
              availability:
                currentStock <= 0
                  ? 'https://schema.org/OutOfStock'
                  : 'https://schema.org/InStock',
            }
          : {}),
        itemOffered: {
          '@type': 'Service',
          name: `Vacation rental stay at ${title}`,
          serviceType: 'Vacation rental accommodation',
        },
      }
    : null;

  const categoryPath = categorySlug ? `/stays/${categorySlug}` : '/s';
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: rootURL },
      {
        '@type': 'ListItem',
        position: 2,
        name: category ? `${category} vacation rentals` : 'Vacation rentals',
        item: `${rootURL}${categoryPath}`,
      },
      { '@type': 'ListItem', position: 3, name: title, item: canonicalURL },
    ],
  };

  return {
    canonicalPath,
    canonicalURL,
    title: seoTitle,
    description: seoDescription,
    category,
    categoryPath,
    schema: [accommodationSchema, ...(offerSchema ? [offerSchema] : []), breadcrumbSchema],
  };
};
